import React from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Button } from './ui/button';
import { Card } from './ui/card';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from './ui/tooltip';
import {
  Edit, Trash2, FileText, ChevronDown, ChevronUp, Eye, Plus,
  History, Settings, Bell, QrCode, AlertCircle, Scissors,
  Check, X, ArrowRight, ArrowLeft, MessageSquare, Loader2, CheckCircle2,
  Merge, Undo2, GripVertical
} from 'lucide-react';
import LoteCardExpandido from './gestor/lotes/LoteCardExpandido';

// Helper: get border color for a lote based on its category
const getLoteBorderColor = (lote) => {
  const coresPadrao = {
    'INT': '#3b82f6',   // blue-500
    'ACA': '#8b5cf6',   // purple-500
    'EMB': '#22c55e',   // green-500
    'PEC': '#f59e0b',   // amber-500
    'PRE': '#6366f1',   // indigo-500
    'PROTO': '#ef4444', // red-500
    'COST': '#ec4899',  // pink-500
  };
  if (lote.categoria_cor) return lote.categoria_cor;
  return coresPadrao[lote.categoria_codigo] || '#22c55e';
};

export default function LotesAprovadosLista({
  lotes = [],
  aprovando = false,
  ofId = '',
  operadores = [],
  handleAlertarOperadores,
  handleEliminarLote,
  handleGerarPDF,
  onEditOperacoes,
  onEditOperacoesLote,
  openAvancarLote,
  openDividirLote,
  openEditObservacoes,
  openHistorico,
  openRetrocederLote,
  openEditarQuantidade,
  getCategoriaCor,
  onReorderLotes,
}) {
  const lotesAprovados = lotes;

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;
    const reordered = Array.from(lotesAprovados);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    if (onReorderLotes) {
      onReorderLotes(reordered.map(l => l.id));
    }
  };

  return (
    <>
{/* Lotes Aprovados */}
      {lotesAprovados.length > 0 && (
        <div>
          <h4 className="font-bold text-green-700 mb-3 flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5" />
            Lotes Aprovados ({lotesAprovados.length})
            <span className="text-xs font-normal text-stone-400 ml-2">Arraste para reordenar</span>
          </h4>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="lotes-aprovados" direction="vertical">
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                >
                  {lotesAprovados.map((lote, index) => (
                    <Draggable key={lote.id} draggableId={lote.id} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          style={provided.draggableProps.style}
                        >
              <Card 
                className={`p-4 border-l-4 hover:shadow-md transition-shadow ${
                  (lote.categoria_codigo === 'ARR' || lote.origem === 'arranjo')
                    ? 'bg-rose-50/60 ring-1 ring-rose-200'
                    : 'bg-green-50/50'
                } ${snapshot.isDragging ? 'shadow-lg ring-2 ring-green-400 opacity-90' : ''}`}
                style={{ borderLeftColor: (lote.categoria_codigo === 'ARR' || lote.origem === 'arranjo') ? '#dc2626' : getLoteBorderColor(lote) }}
                data-testid={`lote-aprovado-${lote.numero_lote}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-2">
                    <div {...provided.dragHandleProps} className="cursor-grab active:cursor-grabbing mt-1" data-testid={`drag-handle-lote-${lote.numero_lote}`}>
                      <GripVertical className="h-5 w-5 text-stone-300 hover:text-stone-500" />
                    </div>
                    <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xl font-bold" style={{ color: getLoteBorderColor(lote) }}>
                        {lote.codigo_lote || `L${lote.numero_lote}`}
                      </span>
                      {lote.categoria_codigo && (
                        <span 
                          className="text-xs px-2 py-0.5 rounded-full border font-medium"
                          style={{ 
                            borderColor: getLoteBorderColor(lote),
                            backgroundColor: `${getLoteBorderColor(lote)}20`,
                            color: getLoteBorderColor(lote)
                          }}
                        >
                          {lote.categoria_nome}
                        </span>
                      )}
                    </div>
                    <p className="text-2xl font-bold text-stone-900">
                      {lote.quantidade_ajustada || lote.quantidade} <span className="text-sm font-normal">peças</span>
                      {lote.quantidade_ajustada && lote.quantidade_ajustada !== lote.quantidade && (
                        <span className="ml-2 text-xs text-sky-600 font-normal" title={`Original: ${lote.quantidade}`}>
                          (ajust. de {lote.quantidade})
                        </span>
                      )}
                    </p>
                    
                    {/* Histórico de contagens se houver */}
                    {lote.historico_contagens && lote.historico_contagens.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-stone-200">
                        <p className="text-xs text-sky-700 font-semibold flex items-center gap-1">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                          {lote.historico_contagens.length} contagem(ns)
                        </p>
                      </div>
                    )}
                    
                    {/* Mostrar quantidade de operações do lote */}
                    {(lote.operacoes_estado || []).length > 0 && (
                      <p className="text-xs text-indigo-600 mt-1 flex items-center gap-1">
                        <Settings className="h-3 w-3" />
                        {lote.operacoes_estado.length} operação(ões) neste lote
                      </p>
                    )}
                    
                    {/* Mostrar quantidade disponível para alocar se houver quantidade alocada */}
                    {(lote.quantidade_alocada_seguinte || 0) > 0 && (
                      <p className="text-xs text-blue-600 mt-1">
                        {lote.quantidade - lote.quantidade_alocada_seguinte} disponíveis | {lote.quantidade_alocada_seguinte} já alocadas
                      </p>
                    )}
                    
                    {lote.origem && lote.origem !== 'novo' && (
                      <p className="text-xs text-purple-600 flex items-center gap-1 mt-1">
                        <Merge className="h-3 w-3" />
                        {lote.origem === 'uniao' ? 'União' : lote.origem === 'mix' ? 'Mix' : lote.origem}
                      </p>
                    )}
                    {lote.lotes_derivados && lote.lotes_derivados.length > 0 && (
                      <p className="text-xs text-blue-600 flex items-center gap-1 mt-1">
                        <ArrowRight className="h-3 w-3" />
                        {lote.lotes_derivados.length} lote(s) derivado(s)
                      </p>
                    )}
                  </div>
                  </div>
                  
                  {/* Ícones de ação no cabeçalho */}
                  <div className="flex items-center gap-2">
                    {/* Botão Operações: ARR abre modal por lote, restantes vão ao gestor da OF */}
                    {(() => {
                      const isArr = lote.categoria_codigo === 'ARR' || lote.origem === 'arranjo';
                      const handle = () => {
                        if (isArr && onEditOperacoesLote) onEditOperacoesLote(lote);
                        else if (onEditOperacoes) onEditOperacoes();
                      };
                      return (
                        <button
                          onClick={handle}
                          className={`px-3 py-1.5 rounded-full text-white text-xs font-bold transition-colors shadow-md ${isArr ? 'bg-rose-600 hover:bg-rose-700' : 'bg-blue-500 hover:bg-blue-600'}`}
                          data-testid={`btn-config-operacoes-of-${lote.numero_lote}`}
                          title={isArr ? 'Editar operações permitidas neste Lote ARR (não afeta OF)' : 'Editar operações da OF'}
                        >
                          {isArr ? 'Ops do Lote' : 'Operações'}
                        </button>
                      );
                    })()}
                    
                    {/* Botão Alertar no cabeçalho */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleAlertarOperadores(lote)}
                      className="border-amber-400 text-amber-600 hover:bg-amber-50 h-8 px-2"
                      data-testid={`btn-alertar-header-lote-${lote.numero_lote}`}
                      title="Alertar operadores"
                    >
                      <Bell className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                {lote.quantidade === lote.quantidade_alocada_seguinte && lote.quantidade > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-2 mb-2">
                    <p className="text-xs text-green-700 flex items-center gap-1">
                      <Check className="h-3 w-3" />
                      Todas as peças alocadas à categoria seguinte
                    </p>
                  </div>
                )}
                
                {lote.lotes_origem_codigos && lote.lotes_origem_codigos.length > 0 && (
                  <p className="text-xs text-stone-500 mb-2">
                    Origem: {lote.lotes_origem_codigos.join(' + ')}
                  </p>
                )}
                
                <p className="text-xs text-stone-500 mb-3">
                  Aprovado em {lote.aprovado_em ? new Date(lote.aprovado_em).toLocaleString('pt-PT') : 'N/A'}
                </p>
                
                {/* Observações - clicável para editar */}
                <button
                  onClick={() => openEditObservacoes(lote)}
                  className="w-full text-left text-sm mb-3 bg-white p-2 rounded border border-dashed border-stone-200 hover:border-primary hover:bg-primary/5 transition-colors cursor-pointer group"
                  title="Clique para editar observações"
                >
                  {lote.observacoes ? (
                    <span className="text-stone-700 italic">{lote.observacoes}</span>
                  ) : (
                    <span className="text-stone-400 flex items-center gap-1">
                      <Edit className="h-3 w-3" />
                      Adicionar observações...
                    </span>
                  )}
                </button>
                
                <div className="flex gap-2 flex-wrap">
                  {/* ARR lotes não têm Avançar/Dividir/Retroceder */}
                  {(() => { const isArr = lote.categoria_codigo === 'ARR' || lote.origem === 'arranjo'; return !isArr; })() && (
                  <>
                  {/* Botão Avançar - apenas se tem quantidade */}
                  {lote.quantidade > 0 && (
                    <Button
                      size="sm"
                      onClick={() => openAvancarLote(lote)}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid={`btn-avancar-lote-${lote.numero_lote}`}
                      title="Avançar para próxima categoria"
                    >
                      <ArrowRight className="h-4 w-4 mr-1" />
                    Avançar
                  </Button>
                  )}
                  
                  {/* Botão Dividir - apenas se tem quantidade > 1 */}
                  {lote.quantidade > 1 && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openDividirLote(lote)}
                      className="border-amber-500 text-amber-600 hover:bg-amber-50"
                      data-testid={`btn-dividir-lote-${lote.numero_lote}`}
                      title="Dividir lote em dois"
                    >
                      <Scissors className="h-4 w-4 mr-1" />
                      Dividir
                    </Button>
                  )}
                  
                  {/* Botão Retroceder - apenas se tem quantidade */}
                  {lote.quantidade > 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openRetrocederLote(lote)}
                      className="border-orange-500 text-orange-600 hover:bg-orange-50"
                      data-testid={`btn-retroceder-lote-${lote.numero_lote}`}
                      title="Retroceder para categoria anterior"
                  >
                    <Undo2 className="h-4 w-4 mr-1" />
                    Retroceder
                  </Button>
                  )}
                  </>
                  )}
                  
                  {/* Botão Acompanhar - Navega para a página de acompanhamento */}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      // Usar window.location para forçar reload completo com os parâmetros
                      window.location.href = `/gestor/ofs?of=${ofId}&tab=acompanhar&lote=${lote.id}`;
                    }}
                    className="border-blue-400 text-blue-600 hover:bg-blue-50"
                    data-testid={`btn-acompanhar-lote-${lote.numero_lote}`}
                    title="Acompanhar produção do lote"
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    Acompanhar
                  </Button>
                  
                  {/* Botão Histórico */}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openHistorico(lote)}
                    className="border-stone-400 text-stone-600 hover:bg-stone-50"
                    data-testid={`btn-historico-lote-${lote.numero_lote}`}
                    title="Ver histórico de movimentação"
                  >
                    <History className="h-4 w-4 mr-1" />
                    Histórico
                  </Button>
                  
                  {/* Botão PDF - Sempre visível para gerar rastreabilidade */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleGerarPDF(lote)}
                    className="border-purple-400 text-purple-600 hover:bg-purple-50"
                    data-testid={`btn-pdf-lote-${lote.numero_lote}`}
                    title="Gerar PDF com rastreabilidade"
                  >
                    <FileText className="h-4 w-4 mr-1" />
                    PDF
                  </Button>
                  
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleEliminarLote(lote.id)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </Card>
                        </div>
                      )}
                    </Draggable>
            ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      )}

      {/* Sem lotes */}
    </>
  );
}
